import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

const MARKER_SOURCE = 'selected-tree-marker'
const RINGS_BEFORE_LAYER = 'unclustered-point'
const MODEL_LAYER_PREFIX = 'trees-3d-'
const RING_LAYER_IDS = ['form-tree-ring-outer', 'form-tree-ring-mid', 'form-tree-ring-inner']

/**
 * Muestra tres aros concéntricos animados sobre el árbol seleccionado (popup abierto).
 * Funciona en cualquier mapa y a cualquier nivel de zoom, incluso cuando el árbol
 * estaría clusterizado en la fuente principal.
 *
 * Usa un source GeoJSON propio (sin cluster) para que el punto siempre esté disponible.
 *
 * @param {{ map, layersReady, selectedCoords: [lng, lat] | null, fixedCoords: [lng, lat] | null }} params
 *   selectedCoords — coordenadas del popup actual (popupLngLat del componente Map)
 *   fixedCoords    — cuando se provee (modo edición), los aros se anclan siempre aquí
 *                    sin importar qué árbol esté en el popup
 */
export const useFormTreeMarker = ({ map, layersReady, selectedCoords, fixedCoords = null }) => {
  const popupId = useSelector(state => state.mapState.popup.id)
  const popupOpen = useSelector(state => state.mapState.popup.open)
  const animFrameRef = useRef(null)

  // En modo edición (fixedCoords provisto) los aros siempre están activos en esa posición.
  // En modo normal se rigen por el popup abierto.
  const isFixed = fixedCoords != null
  const selectedId = isFixed ? 'fixed' : (popupOpen && popupId != null ? popupId : null)
  const activeCoords = isFixed ? fixedCoords : selectedCoords
  const normalizedLng =
    Array.isArray(activeCoords) && activeCoords.length === 2
      ? Number(activeCoords[0])
      : null
  const normalizedLat =
    Array.isArray(activeCoords) && activeCoords.length === 2
      ? Number(activeCoords[1])
      : null
  const hasValidActiveCoords =
    Number.isFinite(normalizedLng) && Number.isFinite(normalizedLat)

  useEffect(() => {
    const isActive = selectedId != null && hasValidActiveCoords
    const canRenderWithoutLayers = isFixed && hasValidActiveCoords
    // Para ACTIVAR los aros necesitamos las capas listas (o modo fijo); para
    // DESACTIVARLOS no, así la limpieza nunca queda bloqueada al cerrar el popup.
    if (!map) return
    if (isActive && !layersReady && !canRenderWithoutLayers) return

    const isStyleReady = () => {
      try {
        return Boolean(map.getStyle?.()) && map.isStyleLoaded()
      } catch {
        return false
      }
    }

    const stopAnimation = () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = null
      }
    }

    const getRingAnchorLayer = () => {
      const layers = map.getStyle?.()?.layers || []
      const firstModelLayerId = layers.find(layer => layer?.id?.startsWith(MODEL_LAYER_PREFIX))?.id
      if (firstModelLayerId && map.getLayer(firstModelLayerId)) return firstModelLayerId
      if (map.getLayer(RINGS_BEFORE_LAYER)) return RINGS_BEFORE_LAYER
      return undefined
    }

    const enforceRingOrder = () => {
      const targetLayer = getRingAnchorLayer()
      if (!targetLayer) return
      RING_LAYER_IDS.forEach(layerId => {
        if (!map.getLayer(layerId)) return
        try {
          map.moveLayer(layerId, targetLayer)
        } catch (_) {}
      })
    }

    const ensureSource = () => {
      try {
        if (!map.getSource(MARKER_SOURCE)) {
          map.addSource(MARKER_SOURCE, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          })
        }
        return true
      } catch (_) {}
      return false
    }

    const ensureLayers = () => {
      const beforeLayer = getRingAnchorLayer()

      if (!map.getLayer('form-tree-ring-outer')) {
        try {
          map.addLayer(
            {
              id: 'form-tree-ring-outer',
              type: 'circle',
              slot: 'top',
              source: MARKER_SOURCE,
              paint: {
                'circle-radius': 23,
                'circle-color': '#ffffff',
                'circle-opacity': 0,
                'circle-stroke-width': 3,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-opacity': 0.9,
                'circle-emissive-strength': 1,
                // Acuesta el aro sobre el plano del mapa para que se incline con el pitch
                'circle-pitch-alignment': 'map',
              },
            },
            beforeLayer
          )
        } catch (_) {}
      }

      if (!map.getLayer('form-tree-ring-mid')) {
        try {
          map.addLayer(
            {
              id: 'form-tree-ring-mid',
              type: 'circle',
              slot: 'top',
              source: MARKER_SOURCE,
              paint: {
                'circle-radius': 16,
                'circle-color': '#ff6d00',
                'circle-opacity': 0,
                'circle-stroke-width': 3,
                'circle-stroke-color': '#ff6d00',
                'circle-stroke-opacity': 1,
                'circle-emissive-strength': 1,
                'circle-pitch-alignment': 'map',
              },
            },
            beforeLayer
          )
        } catch (_) {}
      }

      if (!map.getLayer('form-tree-ring-inner')) {
        try {
          map.addLayer(
            {
              id: 'form-tree-ring-inner',
              type: 'circle',
              slot: 'top',
              source: MARKER_SOURCE,
              paint: {
                'circle-radius': 10,
                'circle-color': '#ffffff',
                'circle-opacity': 0,
                'circle-stroke-width': 3,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-opacity': 0.9,
                'circle-emissive-strength': 1,
                'circle-pitch-alignment': 'map',
              },
            },
            beforeLayer
          )
        } catch (_) {}
      }

      enforceRingOrder()
    }

    const syncSourceData = () => {
      const source = map.getSource(MARKER_SOURCE)
      if (!source) return

      if (selectedId != null && hasValidActiveCoords) {
        source.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [normalizedLng, normalizedLat] },
              properties: { id: selectedId },
            },
          ],
        })
      } else {
        source.setData({ type: 'FeatureCollection', features: [] })
      }
    }

    const startAnimation = () => {
      let startTime = null

      const animate = timestamp => {
        if (!startTime) startTime = timestamp
        const t = ((timestamp - startTime) % 2000) / 2000
        const opacity = 0.7 + 0.3 * Math.sin(t * Math.PI * 2)

        try {
          if (map.getLayer('form-tree-ring-outer')) {
            map.setPaintProperty('form-tree-ring-outer', 'circle-stroke-opacity', opacity)
          }
        } catch (_) {}

        animFrameRef.current = requestAnimationFrame(animate)
      }

      animFrameRef.current = requestAnimationFrame(animate)
    }

    const syncRings = () => {
      // Sin selección activa: desactivar de forma determinista (detener animación
      // y vaciar la fuente) sin depender de que el estilo esté "listo". Evita que
      // el doble anillo quede pegado al cerrar el popup con la X.
      if (!isActive) {
        stopAnimation()
        try {
          const source = map.getSource(MARKER_SOURCE)
          if (source) source.setData({ type: 'FeatureCollection', features: [] })
        } catch (_) {}
        return
      }
      if (!isStyleReady()) return
      if (!ensureSource()) return
      ensureLayers()
      syncSourceData()
      stopAnimation()
      startAnimation()
    }

    syncRings()
    map.on('styledata', syncRings)
    map.on('style.load', syncRings)

    return () => {
      map.off('styledata', syncRings)
      map.off('style.load', syncRings)
      stopAnimation()
    }
  }, [map, layersReady, isFixed, selectedId, hasValidActiveCoords, normalizedLng, normalizedLat])
}

export default useFormTreeMarker
